import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import { Form, Formik, FormikHelpers } from 'formik'
import { Modal } from '@mui/material'
import { CalendarMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import { getExpirationMinDate } from 'src/utils/expirationDateUtils'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Button } from 'src/components/shared/Button'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { WarningContainer } from 'src/components/shared/WarningContainer'
import { FormikDatePicker } from 'src/components/shared/form/formik/FormikDatePicker'
import { ApproveOnboardingStep } from 'src/constants/actionTypes'
import NotFoundImage from 'src/assets/images/not-found.png'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { useDispatch } from 'react-redux'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { ApproveStepFormValues } from './ApproveStepForm.interface'
import { canSetDatesOnApprove } from './approveStepUtils'
import { ApproveStepFormSchema } from './ApproveStepForm.validator'

interface Props {
  isOpen: boolean
  collectedData: CollectedData
  contractorDetails: ContractorDetails
  isCombinedStep?: boolean
  onClose(): void
  onSuccess(): void | Promise<void>
}

const ApproveStepModalComponent = ({
  isOpen,
  collectedData,
  contractorDetails,
  isCombinedStep = false,
  onClose,
  onSuccess,
}: Props) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const pendingValuesRef = useRef<ApproveStepFormValues | null>(null)

  const container = useMemo(() => document.getElementById('root'), [])

  const canSetDates = canSetDatesOnApprove(collectedData, isCombinedStep)

  const initialValues: ApproveStepFormValues = useMemo(() => {
    let effectiveAt: string | null = null
    let expiresAt: string | null = null

    if (canSetDates && collectedData.effectiveAt != null) {
      effectiveAt = collectedData.effectiveAt
    }

    if (canSetDates && collectedData.expiresAt != null) {
      expiresAt = collectedData.expiresAt
    }

    return { effectiveAt, expiresAt }
  }, [canSetDates, collectedData.effectiveAt, collectedData.expiresAt])

  const submitValues = useCallback(
    async (values: ApproveStepFormValues) => {
      setIsLoading(true)

      try {
        const requestPayload: {
          stepId: string
          partnerDriverId: string
          effectiveAt?: string
          expiresAt?: string
        } = {
          stepId: collectedData.collectDataStep.id,
          partnerDriverId: contractorDetails.id,
        }

        if (canSetDates && values.effectiveAt) {
          requestPayload.effectiveAt = values.effectiveAt
        }

        if (canSetDates && values.expiresAt) {
          requestPayload.expiresAt = values.expiresAt
        }

        await createAsyncAction(dispatch, ApproveOnboardingStep.request(requestPayload))

        await onSuccess()
        onClose()
        showToast('Step is approved.')
      } catch (err) {
        handleApiError(err)
      } finally {
        setIsLoading(false)
      }
    },
    [canSetDates, collectedData, contractorDetails, dispatch, onSuccess, onClose],
  )

  const handleSubmit = useCallback(
    (values: ApproveStepFormValues, formikHelpers: FormikHelpers<ApproveStepFormValues>) => {
      void formikHelpers

      if (canSetDates && !values.effectiveAt) {
        pendingValuesRef.current = values
        setIsConfirmModalOpen(true)

        return
      }

      return submitValues(values)
    },
    [canSetDates, submitValues],
  )

  const handleConfirmWithoutEffectiveDate = useCallback(async () => {
    const values = pendingValuesRef.current

    if (values) {
      setIsConfirmModalOpen(false)
      pendingValuesRef.current = null

      await submitValues(values)
    }
  }, [submitValues])

  return (
    <>
      <Modal
        container={container}
        open={isOpen}
        onClose={onClose}
        aria-labelledby="approve-step-modal-title"
        css={tw`flex justify-center items-center backdrop-blur`}
      >
        <div css={tw`bg-white rounded-lg w-[360px]`}>
          <ModalHeader title="Approve step" onClose={onClose} />
          <div css={tw`px-4 pt-0 pb-3`}>
            {canSetDates && (
              <div css={tw`mb-4 mt-4`}>
                <WarningContainer
                  title="Important — Effective Date"
                  subtitle="If you do not set an effective date, new submission will replace the current one and become active immediately. The replaced submission will no longer be accessible."
                />
              </div>
            )}
            <Formik
              initialValues={initialValues}
              validationSchema={ApproveStepFormSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ handleSubmit: formikHandleSubmit, values }) => {
                const expirationMinDateOptions = getExpirationMinDate(values.effectiveAt)

                return (
                  <Form css={tw`flex flex-col`}>
                    <div css={tw`flex flex-col gap-4`}>
                      {canSetDates && (
                        <div css={tw`w-full`}>
                          <FormikDatePicker
                            name="effectiveAt"
                            label="Set effective date (optional)"
                            minDate={dayjs().add(1, 'day')}
                            minDateError="Effective date should be at least 1 day in the future"
                            slotProps={{
                              textField: { FormHelperTextProps: { variant: 'standard' } },
                            }}
                            slots={{
                              openPickerIcon: (props) => (
                                <CalendarMinor {...props} width={20} height={20} />
                              ),
                            }}
                          />
                        </div>
                      )}
                      {canSetDates && (
                        <div css={tw`w-full`}>
                          <FormikDatePicker
                            name="expiresAt"
                            label="Set expiration date (optional)"
                            minDate={expirationMinDateOptions.minDate}
                            minDateError={expirationMinDateOptions.minDateError}
                            slotProps={{
                              textField: { FormHelperTextProps: { variant: 'standard' } },
                            }}
                            slots={{
                              openPickerIcon: (props) => (
                                <CalendarMinor {...props} width={20} height={20} />
                              ),
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div
                      css={tw`flex justify-between items-center mt-4 pt-3 border-0 border-t border-solid border-[#EDEDED]`}
                    >
                      <Button
                        size="small"
                        color="grey"
                        onClick={onClose}
                        disabled={isLoading}
                        className={tw`w-[100px]`}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => formikHandleSubmit()}
                        loading={isLoading}
                      >
                        Approve & Set
                      </Button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      </Modal>

      <SensitiveActionModal
        isOpen={isConfirmModalOpen}
        title="Approve without Effective Date?"
        text="This will permanently replace the current submission and remove access to it. Please confirm to proceed."
        submitLabel="Confirm"
        cancelLabel="Back"
        color="primary"
        className={tw`w-[330px] min-w-[330px]`}
        image={
          <img src={NotFoundImage} alt="Question" css={tw`w-[210px] h-[164px] object-contain`} />
        }
        onClose={() => setIsConfirmModalOpen(false)}
        onSubmit={handleConfirmWithoutEffectiveDate}
      />
    </>
  )
}

export const ApproveStepModal = memo(ApproveStepModalComponent)
